<template>
    <div class="c-widget">
        <component
            v-if="type"
            :is="type"
            @sort="onSort"
            :configuration="configuration"
            :loading="loading"
            :rows="dataRows"
            :columns="dataColumns"
            :sort-by="sortBy"
            :border="false"
        >
        </component>

        <Footer class="c-widget__paging_footer">
            <Pagination
                :current="pageCurrent"
                :total="pageTotal"
                :page-size="pageSize"
                @page-change="onPageChange"
                @page-size-change="onPageSizeChange"
                show-sizer
                :page-size-opts="[10, 25, 50, 100]"
            />
        </Footer>
    </div>
</template>
<script>
export default {
    props: {
        definition: Object,
        modifiers: Array,
        configuration: Object,
        segmentsRepository: {
            type: Object,
            default: () => {
                return new (require("@/js/repositories/segments.js"))();
            },
        },
    },
    data() {
        return {
            data: null,
            segment: null,
            loading: false,
            pageCurrent: 1,
            pageSize: 25,
            sortBy: null,
            selectedColumns: [],
            localStorageKeySortBy:
                "segments_" + this.definition.segmentId + "_sortBy",
        };
    },
    components: {
        AccountsTable: function () {
            return import("@/js/components/partials/accounts-table");
        },
        ContactsTable: function () {
            return import("@/js/components/partials/contacts-table");
        },
        Pagination: function () {
            return import("@/js/components/partials/pagination");
        },
    },
    created() {
        this.query = _.debounce(this.query, 1000, {
            leading: false,
            trailing: true,
        });
        this.load();
    },
    watch: {
        definition: {
            handler: function () {
                this.init();
            },
            deep: true,
        },
        modifiers(newVal, oldVal) {
            this.init();
        },
    },
    computed: {
        segmentId() {
            return _.get(this.definition, "segmentId");
        },
        valid() {
            return true;
        },
        type() {
            return 1 == _.get(this.segment, "entity_type_id")
                ? "AccountsTable"
                : "ContactsTable";
        },
        dataColumns() {
            let columnData = _.get(this.data, "columns", []);
            return _.map(columnData, function (column) {
                return Object.assign(column, {
                    title: column.label,
                });
            });
        },
        dataRows() {
            return _.get(this.data, "data", []);
        },
        pageTotal() {
            return _.get(this.data, "meta.total", 0);
        },
        sortByFormatted() {
            // There should be a default set but if not, return null
            if (!_.get(this, "sortBy.column")) {
                return null;
            }

            let parts = [
                this.sortBy.column,
                this.sortBy.order || "asc",
                this.sortBy.dataTypeId,
            ];

            return parts.filter(Boolean).join(":");
        },
    },
    methods: {
        async load() {
            await this.querySegment();
            this.initializeSortBy();
            this.initializeColumns();
            this.query();
        },
        initializeColumns() {
            var definition = JSON.parse(
                _.get(this.segment, "definition", "{}")
            );
            this.selectedColumns = _.get(definition, "columns", []);
            this.$watch("selectedColumns", this.onColumnsChange, {
                deep: true,
            });
        },
        initializeSortBy() {
            var sortBy = this.$localStorageCache.get(
                this.localStorageKeySortBy
            );
            var definition = JSON.parse(
                _.get(this.segment, "definition", "{}")
            );
            this.sortBy =
                JSON.parse(sortBy) ||
                _.get(definition, "sortBy", {
                    column: "orgs__name",
                    order: "asc",
                    dataTypeId: "1",
                });
            this.$watch("sortBy", this.onSortByChange, { deep: true });
        },
        async querySegment() {
            let segmentResponse = await this.segmentsRepository.show(
                this.definition.segmentId,
                {
                    params: {
                        include: [],
                    },
                }
            );
            this.segment = segmentResponse.data.data;
        },
        async query() {
            this.loading = true;
            let request = {
                from_cache: false,
                columns: JSON.stringify(this.selectedColumns),
                sort_by: this.sortByFormatted,
                page: this.pageCurrent,
                per_page: this.pageSize,
            };
            let response;
            try {
                response = await this.segmentsRepository
                    .entities(this.segmentId)
                    .index(request);
            } catch (e) {
                this.loading = false;
                this.data = [];
                this.pageCurrent = 1;
                _.merge(this.sortBy, {
                    column: null,
                    order: null,
                    dataTypeId: null,
                });
                return;
            }
            this.loading = false;
            this.data = response.data;
            this.pageCurrent = _.get(response.data, "meta.current_page", 1);
            this.pageSize = _.get(response.data, "meta.per_page", 25);
        },
        onPageChange(page) {
            this.pageCurrent = page;
            this.query();
        },
        onPageSizeChange(size) {
            this.pageCurrent = 1;
            this.pageSize = size;
            this.query();
        },
        onSort(definition) {
            this.pageCurrent = 1;
            /**
             * Clicking in the table sort gives us access to a key
             * but we need the column.property
             */
            var column = _.find(this.columns, { key: definition.key });
            if (column) {
                /**
                 * Two scenarios:
                 * - 1 - Changing Sort Direction (Same column)
                 * - 2 - New Column
                 */
                if (_.get(this, "sortBy.column") == column.property) {
                    _.merge(this.sortBy, {
                        order: definition.order,
                    });
                } else {
                    _.merge(this.sortBy, {
                        column: column.property,
                        order: definition.order,
                        dataTypeId: column.genericDataTypeId,
                    });
                }
                return;
            }
            /**
             * If the column doesn't exist, it is likely name
             */
            _.merge(this.sortBy, {
                column: "orgs__name",
                order: definition.order,
                dataTypeId: "1",
            });
        },
        onSortByChange() {
            this.$localStorageCache.set(
                this.localStorageKeySortBy,
                JSON.stringify(this.sortBy)
            );
            this.query();
        },
        onOwnerChange() {
            this.$localStorageCache.set(this.localStorageKeyOwner, this.owner);
            this.query();
        },
    },
};
</script>
